import React, { FC, useCallback, useState } from "react";
import { useLocation } from 'react-router-dom';

import { generateFollowThroughPattern } from "@xapp/arachne-utils";
import {
    Handler,
    App as StentorApp,
    Entity as StentorEntity,
    Handler as StentorHandler,
    Intent as StentorIntent,
    APP_STATUS_READY_FOR_INSTALL
} from "stentor-models";
import { hashCode } from "stentor-utils";

import { INTELLIGENT_SEARCH, GOOGLE_BUSINESS_MESSAGES } from "constants/channels";
import {
    CreateOrgAndAppOnboardingMutationVariables,
    useCreateOrgAndAppOnboardingMutation,
    useAddChatWidgetChannelMutation,
    useStartCrawlMutation,
    useAddLexV2ChannelMutation,
    useAddFormWidgetChannelMutation,
    useAddGoogleBusinessChannelMutation,
    useUpdateSearchChannelMutation, // can be used for adding or updated
    useUpdateApplicationMutation,
    useUpdateStatusMutation,
    UpdateAppInput,
    useUpdateEmailOpportunityAlertsMutation,
    OpportunityAlertDetailType
} from "generated/graphql";
import { ClientApp } from "models";
import { ChatWidgetInput, generateCreateAppWidgetInput } from "utils/generateCreateAppWidgetInput";
import { FormWidgetInput, generateFormWidgetInput } from "utils/generateFormWidgetInput";
import { wrapUrqlMutation, convertHandler } from "utils/graphql";
import { useBoolState } from "utils/bool";

import { ConfigureAndCreateStep, ConfigureAndCreateStepProps } from "../components/ConfigureAndCreateStep";
import { CreateProps } from "../models";

import { getProducts } from "utils/getProducts";
import { getApiUrl, getRouterUrl } from "utils/getApiUrl";
import { generateFormsData } from "utils/generateFormsData";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

export interface ConfigureAndCreateStepContainerProps extends Omit<ConfigureAndCreateStepProps, "onCreate"> {
    /**
     * Selected product
     */
    product: string;
    /**
     * The user is ready for the next step
     * @param app
     */
    onNextStep(app: Pick<ClientApp, "appId" | "organizationId">): void;
}

export const ConfigureAndCreateStepContainer: FC<ConfigureAndCreateStepContainerProps> = props => {

    const { onNextStep, product: productId } = props;

    const queryParams = useQueryParams();

    const [creationStatusMessage, setCreationStatusMessage] = useState<string>();
    const [creating, stopCreating, startCreating] = useBoolState(false);
    const [error, setError] = useState<{ error: string, message: string }>();

    const products = getProducts();
    const product = products.find((product) => product.id === productId);

    /**
     * Define some mutations we will use during creation.
     *
     * We want to suppress the global alerts and errors and instead display them on the page itself
     */
    const createApp = wrapUrqlMutation(useCreateOrgAndAppOnboardingMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the app for you." });
            return { send: false }
        }
    });

    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the contact information on the app for you." });
            return { send: false }
        }
    });

    const updateEmailOpportunityAlerts = wrapUrqlMutation(useUpdateEmailOpportunityAlertsMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating an opportunity alert for you." });
            return { send: false }
        }
    });

    const updateStatus = wrapUrqlMutation(useUpdateStatusMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the status for you." });
            return { send: false }
        }
    });

    const createWidget = wrapUrqlMutation(useAddChatWidgetChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the widget channel for you." });
            return { send: false }
        }
    });

    const updateWidget = wrapUrqlMutation(useAddChatWidgetChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the widget channel for you." });
            return { send: false }
        }
    });

    const createSearch = wrapUrqlMutation(useUpdateSearchChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating a search channel for you." });
            return { send: false }
        }
    });

    const updateSearch = wrapUrqlMutation(useUpdateSearchChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating creating a search widget for you." });
            return { send: false }
        }
    });

    const createGBM = wrapUrqlMutation(useAddGoogleBusinessChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating a Google Business Messages channel for you." });
            return { send: false }
        }
    });

    const createScheduler = wrapUrqlMutation(useAddFormWidgetChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the scheduler channel for you." });
            return { send: false }
        }
    });

    /*
    const updateGBM = wrapUrqlMutation(useAddGoogleBusinessChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating your Google Business Messages channel for you." });
            return { send: false }
        }
    }); */

    const createLexV2Channel = wrapUrqlMutation(useAddLexV2ChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the NLU channel for you." });
            return { send: false }
        }
    });

    const startCrawl = wrapUrqlMutation(useStartCrawlMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble starting the website crawl for you." });
            return { send: false }
        }
    });

    /**
     * The main method fo app creation, it all of the mutations.
     *
     * We will want to refactor this to make it more potable and reusable.
     */
    const handleCreate = useCallback(async (createProps: CreateProps) => {
        startCreating();

        // Update the current fields in case of an error
        sessionStorage.setItem("onboarding-appProps", JSON.stringify(createProps));

        // load the template JSON
        setCreationStatusMessage("Preparing data...");
        const templateUrl = createProps.template.url;
        const templateId = createProps.template.id;

        type Template = { app: StentorApp, entities: StentorEntity[], intents: StentorIntent[], handlers: StentorHandler[] };

        const template: Template = await fetch(templateUrl)
            .then((response) => {
                return response.json();
            }).catch((e) => {
                setError({ message: `Could not load the template: ${templateId}`, error: e.message });
                stopCreating();
            });

        if (!template) {
            // bail
            return;
        }
        setCreationStatusMessage(`Template ${template.app.name} loaded...`)
        /**
         * No model for our templates so we clean off utterances & don't pass intents & entities
         */
        const cleanHandler = (handler: Handler): Handler => {
            // delete slots
            const cleaned = { ...handler };
            cleaned.slots = [];
            cleaned.utterancePatterns = [];

            return cleaned;
        }

        const handlers = template.handlers.map(cleanHandler).map(convertHandler);

        const contactCaptureHandler = handlers.find((handler) => {
            return handler.intentId === "LeadGeneration";
        });

        const contactCaptureResponses = contactCaptureHandler.content.find((content) => {
            return content.key === "LeadGeneration";
        });

        contactCaptureResponses.handlerResponse.push({
            outputSpeech: {
                ssml: `<speak>Sure, please contact us at ${createProps.leadsContactPhone} or ${createProps.leadsContact}.</speak>`,
                displayText: `Sure, please contact us at ${createProps.leadsContactPhone} or ${createProps.leadsContact}.`,
                suggestions: []
            },
            name: "No Capture Start",
            tag: "ContactCaptureNoCaptureStart"
        });

        const mainFormName = "scheduling_form_v1";
        // add the form metadata
        contactCaptureHandler.data["CAPTURE_MAIN_FORM"] = mainFormName;
        const mainForm = generateFormsData({
            name: mainFormName, options: [{
                type: "CONTACT_US",
                label: "Contact Us"
            }]
        });
        contactCaptureHandler.data.forms = [mainForm];

        const variables: CreateOrgAndAppOnboardingMutationVariables = {
            app: {
                app: {
                    name: createProps.name,
                    businessDescription: createProps.businessDescription,
                    website: createProps.website,
                    templateType: createProps.template.id,
                    icon: createProps.logo
                    // TODO: Ability to add leadsContact & leadsPhoneContact & website
                },
                // they are empty so we don't create a model
                entities: [],
                intents: [],
                handlers
            },
            org: {
                name: createProps.name,
                logoUrl: createProps.logo
            }
        };

        // TODO: Update org with contact info

        setCreationStatusMessage("Creating Assistant...");

        const result = await createApp(variables);

        const app = result?.data?.org?.add?.app;

        if (!app) {
            stopCreating();
            return;
        }

        const { appId } = app;

        // Update with a few fields
        setCreationStatusMessage("Updating contact information...");

        const updateAppInput: UpdateAppInput = {
            website: createProps.website,
            leadsContact: createProps.leadsContact,
            leadsContactPhone: createProps.leadsContactPhone
        }

        const partner: string | undefined = queryParams.get("partner") || product?.metadata?.["partner"];

        if (partner) {
            updateAppInput.refernceId = {
                service: partner,
                // we don't know this yet
                id: "unknown"
            }
        }

        await updateApplication({
            appId,
            app: updateAppInput
        });

        const supportsLiveChat = product?.metadata?.['live-chat'] === "true";

        if (supportsLiveChat) {
            setCreationStatusMessage("Setting up opportunity alerts for live chat...");
            await new Promise(r => setTimeout(r, 200));
            await updateEmailOpportunityAlerts({
                appId,
                alert: {
                    email: createProps.leadsContact,
                    alerts: [
                        {
                            duringBusinessHours: true,
                            outsideBusinessHours: true,
                            type: OpportunityAlertDetailType.NewSession
                        }
                    ]
                }
            })
        }

        await updateStatus({
            appId,
            type: APP_STATUS_READY_FOR_INSTALL,
            notes: `Assistant created ${new Date().toDateString()} during onboarding.`
        });

        // Create Chat Widget & Search Bar (& GBM);
        setCreationStatusMessage("Configuring channels...");
        await new Promise(r => setTimeout(r, 200));

        // START WIDGET CREATE
        const chatWidgetProps: ChatWidgetInput = {
            ...createProps,
            appId
        }

        const chatWidgetInput = generateCreateAppWidgetInput(chatWidgetProps);

        // if not free tier, delete the branding
        if (product) {
            delete chatWidgetInput.footer;
        }

        const createWidgetResponse = await createWidget({ appId, channel: chatWidgetInput });

        const widget = createWidgetResponse?.data?.addChatWidgetChannel;

        if (!widget) {
            setError({ message: `Could not create chat widget.`, error: `Widget was undefined` });
            stopCreating();
            return;
        }

        // Update URLs
        setCreationStatusMessage("Polishing channels...");
        await new Promise(r => setTimeout(r, 200));

        const apiUrl = getApiUrl();
        const wssUrl = getRouterUrl();

        chatWidgetInput.id = widget.id;
        // Update some URLs that required the key and ID which are only available after creation.
        chatWidgetInput.autocompleteSuggestionsUrl = `${apiUrl}/chat/suggestions?key=${widget.key}&type=INTENT,FAQ&templateAppId=${createProps.template.id}`;
        chatWidgetInput.connection = {
            serverUrl: `${wssUrl}/?appId=${appId}&channelId=${widget.id}`,
            type: "websocketraw",
        }
        chatWidgetInput.serverUrl = `${apiUrl}/apps/${appId}/run`;

        await updateWidget({ appId, channel: chatWidgetInput });
        // WIDGET DONE

        // We are just going to make both channels and prevent access to them
        // based on current plan
        // const supportSearch: boolean = product?.metadata?.search === "true";
        // const supportGBM: boolean = product?.metadata?.gbm === "true";

        // create searchChannel
        const searchChannelResult = await createSearch({
            appId,
            channel: {
                type: INTELLIGENT_SEARCH,
                name: "Intelligent Search"
            }
        });

        const searchChannel = searchChannelResult.data.app.update.channel.addIntelligentSearchChannel;
        delete searchChannel.__typename;

        // Update it!
        await updateSearch({
            appId,
            channel: {
                ...searchChannel,
                connection: {
                    serverUrl: `${apiUrl}/apps/${appId}/run`
                },
                autocompleteSuggestionsUrl: `${apiUrl}/chat/suggestions?key=${searchChannel.key}&type=FAQ`,
                theme: {
                    accentColor: createProps.primaryColor
                }
            }
        });

        const hash = `${hashCode(`${createProps.name}-${new Date().getTime()}`)}`.substring(0, 5);
        // GBM ID
        const id = `gbm-${hash}`;

        await createGBM({
            appId,
            channel: {
                type: GOOGLE_BUSINESS_MESSAGES,
                name: "Google Business Messages",

                // TODO: when available, have the server set this
                id,
                // endpoint: we are waiting on this until we get the new POST endpoint
                endPoint: `${apiUrl}/apps/router?appId=${appId}&channelId=${id}`
                // TODO: avatarUrl
            }
        });

        // Create Schedular!
        const appFormInput: FormWidgetInput = {
            ...createProps,
            appId
        };

        const schedularInput = generateFormWidgetInput(appFormInput);

        await createScheduler({
            appId,
            channel: schedularInput
        });

        setCreationStatusMessage("Training model...");
        await new Promise(r => setTimeout(r, 200));
        // LEX v2
        await createLexV2Channel({
            appId,
            channel: {
                type: "lex-v2",
                botName: templateId
            }
        });

        // Start Crawl
        setCreationStatusMessage("Starting website ingestion...");
        await new Promise(r => setTimeout(r, 200));

        const pattern: string[] = [generateFollowThroughPattern(createProps.website)];

        await startCrawl({
            appId,
            url: createProps.website,
            pattern,
            channelId: widget.id
        });

        // Done
        setCreationStatusMessage(`All done!`);
        // Delay for 1.5 s
        await new Promise(r => setTimeout(r, 200));

        stopCreating();

        // Move on!
        onNextStep(app);
    }, [
        createApp,
        createGBM,
        createLexV2Channel,
        createScheduler,
        createSearch,
        createWidget,
        onNextStep,
        product,
        queryParams,
        startCrawl,
        startCreating,
        stopCreating,
        updateApplication,
        updateEmailOpportunityAlerts,
        updateSearch,
        updateStatus,
        updateWidget
    ]);

    return (
        <ConfigureAndCreateStep
            {...props}
            creating={creating}
            creatingStatusMessage={creationStatusMessage}
            onCreate={handleCreate}
            error={error}
        />
    );
}